import React, { useState, useEffect, useMemo } from 'react';
import { Calculator, TrendingDown, Info, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuoteCalculator = ({ language, onServiceSelect, region = 'pt' }) => {
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState('');
  const [quantity, setQuantity] = useState('');
  const [showCalculator, setShowCalculator] = useState(true); // Start expanded
  const [calculated, setCalculated] = useState(null);

  useEffect(() => {
    fetchPricing();
  }, [region]);

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API}/pricing?region=${region}`);
      setPricing(response.data);
    } catch (error) {
      console.error('Error fetching pricing:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get region display name
  const getRegionName = () => {
    return region === 'ie' ? 'Dublin' : 'Faro';
  };

  const selectedPricing = useMemo(() => {
    return pricing.find(p => p.service_key === selectedService);
  }, [pricing, selectedService]);

  const handleServiceChange = (e) => {
    const serviceKey = e.target.value;
    setSelectedService(serviceKey);
    setQuantity('');
    setCalculated(null);
    
    // Find the service name and pass to parent
    const service = pricing.find(p => p.service_key === serviceKey);
    if (service && onServiceSelect) {
      onServiceSelect(language === 'pt' ? service.service_name_pt : service.service_name_en);
    }
  };

  const handleQuantityChange = (e) => {
    const value = e.target.value;
    setQuantity(value);
    
    if (value && selectedPricing) {
      calculateQuote(parseFloat(value));
    } else {
      setCalculated(null);
    }
  };

  const calculateQuote = (qty) => {
    if (!selectedPricing || !qty || qty <= 0) {
      setCalculated(null);
      return;
    }

    const marketPrice = selectedPricing.market_average_price;
    const discountPercent = selectedPricing.discount_percentage;
    const minimumPrice = selectedPricing.minimum_price;

    // Calculate our price with discount
    let ourPricePerUnit = marketPrice * (1 - discountPercent / 100);
    
    // Ensure we don't go below minimum
    ourPricePerUnit = Math.max(ourPricePerUnit, minimumPrice);

    // Calculate totals
    const marketTotal = marketPrice * qty;
    const ourTotal = ourPricePerUnit * qty;
    const savings = marketTotal - ourTotal;
    const savingsPercent = ((savings / marketTotal) * 100).toFixed(1);

    setCalculated({
      marketPricePerUnit: marketPrice,
      ourPricePerUnit: ourPricePerUnit,
      marketTotal: marketTotal,
      ourTotal: ourTotal,
      savings: savings,
      savingsPercent: savingsPercent,
      quantity: qty
    });
  };

  const getUnitLabel = () => {
    if (!selectedPricing) return '';
    return language === 'pt' ? selectedPricing.unit_label_pt : selectedPricing.unit_label_en;
  };

  const getQuantityLabel = () => {
    if (!selectedPricing) return language === 'pt' ? 'Quantidade' : 'Quantity';
    
    const type = selectedPricing.calculation_type;
    if (type === 'per_m2') return language === 'pt' ? 'Área (m²)' : 'Area (m²)';
    if (type === 'per_hour') return language === 'pt' ? 'Horas estimadas' : 'Estimated hours';
    if (type === 'per_unit') return language === 'pt' ? 'Número de unidades' : 'Number of units';
    return language === 'pt' ? 'Quantidade' : 'Quantity';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  if (loading) {
    return (
      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
        <p className="text-sm text-gray-500">{language === 'pt' ? 'Carregando calculadora...' : 'Loading calculator...'}</p>
      </div>
    );
  }

  return (
    <div className="border border-primary/20 rounded-lg overflow-hidden bg-gradient-to-br from-primary/5 to-white">
      {/* Header - Toggle */}
      <button
        type="button"
        onClick={() => setShowCalculator(!showCalculator)}
        className="w-full px-4 py-3 flex items-center justify-between bg-primary/10 hover:bg-primary/15 transition-colors"
        data-testid="calculator-toggle"
      >
        <div className="flex items-center gap-2">
          <Calculator size={20} className="text-primary" />
          <span className="font-medium text-primary">
            {language === 'pt' ? 'Calculadora de Orçamento' : 'Quote Calculator'}
          </span>
          <span className="text-xs text-primary/70 ml-2">
            {language === 'pt' ? '(Estimativa instantânea)' : '(Instant estimate)'}
          </span>
        </div>
        {showCalculator ? <ChevronUp size={20} className="text-primary" /> : <ChevronDown size={20} className="text-primary" />}
      </button>

      {/* Calculator Body */}
      {showCalculator && (
        <div className="p-4 space-y-4">
          {/* Service Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {language === 'pt' ? 'Selecione o serviço' : 'Select service'}
            </label>
            <select
              value={selectedService}
              onChange={handleServiceChange}
              className="w-full h-11 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              data-testid="calculator-service-select"
            >
              <option value="">{language === 'pt' ? '-- Escolha um serviço --' : '-- Choose a service --'}</option>
              {pricing.map(p => (
                <option key={p.service_key} value={p.service_key}>
                  {language === 'pt' ? p.service_name_pt : p.service_name_en}
                </option>
              ))}
            </select>
          </div>

          {/* Service Description - if available */}
          {selectedService && selectedPricing && (selectedPricing.description_en || selectedPricing.description_pt) && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <span className="font-medium">ℹ️ </span>
                {language === 'pt' ? selectedPricing.description_pt : selectedPricing.description_en}
              </p>
            </div>
          )}

          {/* Quantity Input */}
          {selectedService && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {getQuantityLabel()}
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={quantity}
                  onChange={handleQuantityChange}
                  placeholder={language === 'pt' ? 'Digite o valor...' : 'Enter value...'}
                  className="w-full h-11 px-3 pr-16 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  data-testid="calculator-quantity-input"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                  {getUnitLabel()}
                </span>
              </div>
            </div>
          )}

          {/* Results */}
          {calculated && (
            <div className="space-y-4 pt-4 border-t border-gray-200">
              {/* Our Price - Highlighted */}
              <div className="bg-primary text-white rounded-xl p-4 text-center">
                <p className="text-sm opacity-80 mb-1">
                  {language === 'pt' ? 'Valor Estimado Sancur' : 'Estimated Sancur Price'}
                </p>
                <p className="text-3xl font-bold" data-testid="calculator-our-total">
                  {formatCurrency(calculated.ourTotal)}
                </p>
                <p className="text-xs opacity-70 mt-1">
                  {formatCurrency(calculated.ourPricePerUnit)} / {getUnitLabel()}
                </p>
              </div>

              {/* Market Comparison */}
              <div className="bg-gray-100 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-gray-600">
                    {language === 'pt' ? `Média de Mercado (${getRegionName()})` : `Market Average (${getRegionName()})`}
                  </span>
                  <span className="text-lg font-semibold text-gray-700 line-through" data-testid="calculator-market-total">
                    {formatCurrency(calculated.marketTotal)}
                  </span>
                </div>
                
                {/* Savings */}
                <div className="flex items-center justify-between bg-green-100 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <TrendingDown size={20} className="text-green-600" />
                    <span className="text-sm font-medium text-green-800">
                      {language === 'pt' ? 'Economia Estimada' : 'Estimated Savings'}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-bold text-green-700" data-testid="calculator-savings">
                      {formatCurrency(calculated.savings)}
                    </span>
                    <span className="text-xs text-green-600 ml-1">
                      (-{calculated.savingsPercent}%)
                    </span>
                  </div>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="flex items-start gap-2 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <Info size={16} className="text-yellow-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-yellow-800">
                  {language === 'pt' 
                    ? `Este valor é apenas uma estimativa e poderá ser ajustado após vistoria técnica no local. Os preços de mercado são baseados em médias da região de ${getRegionName()}.`
                    : `This is only an estimate and may be adjusted after technical inspection on site. Market prices are based on ${getRegionName()} region averages.`}
                </p>
              </div>
            </div>
          )}

          {/* Empty state hint */}
          {selectedService && !quantity && (
            <p className="text-sm text-gray-500 text-center py-2">
              {language === 'pt' 
                ? 'Digite a quantidade para ver o orçamento estimado' 
                : 'Enter quantity to see estimated quote'}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default QuoteCalculator;
